import React, { useState } from 'react';
import './AttendanceQuestionsInspector.css';
import MessageSectionInspector from './MessageSectionInspector';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

function AttendanceQuestionsInspector({ 
  func, 
  index, 
  onUpdate,
  isIconPickerOpen,
  setIsIconPickerOpen,
  iconPickerPosition,
  setIconPickerPosition,
  setActiveIconPreview,
  bringToFront
}) {
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [messageSettingsExpanded, setMessageSettingsExpanded] = useState(false);
  const [questionsExpanded, setQuestionsExpanded] = useState(false);

  const handleMessageSectionUpdate = (_, updatedProps) => {
    onUpdate(index, {
      ...func.props,
      messageProps: updatedProps
    });
  };

  const handleQuestionChange = (questionIndex, field, value) => {
    const newQuestions = [...(func.props.questions || [])];
    newQuestions[questionIndex] = {
      ...newQuestions[questionIndex],
      [field]: value
    };
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const handleDependencyChange = (questionIndex, dependencyIndex, field, value) => {
    const newQuestions = [...(func.props.questions || [])];
    if (!newQuestions[questionIndex].dependencies) {
      newQuestions[questionIndex].dependencies = [];
    }
    if (!newQuestions[questionIndex].dependencies[dependencyIndex]) {
      newQuestions[questionIndex].dependencies[dependencyIndex] = {};
    }

    // If changing questionId, reset the dependency attributes
    if (field === 'questionId') {
      const targetQuestion = func.props.questions.find(q => q.id === value);
      newQuestions[questionIndex].dependencies[dependencyIndex] = {
        questionId: value,
        // Initialize appropriate attribute based on question type
        ...(targetQuestion?.type === 'open' ? { expectedLength: 1 } : { expectedResponse: '' })
      };
    } else {
      newQuestions[questionIndex].dependencies[dependencyIndex] = {
        ...newQuestions[questionIndex].dependencies[dependencyIndex],
        [field]: value
      };
    }
    
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const addDependency = (questionIndex) => {
    const newQuestions = [...(func.props.questions || [])];
    if (!newQuestions[questionIndex].dependencies) {
      newQuestions[questionIndex].dependencies = [];
    }
    // Don't initialize with expectedLength by default
    newQuestions[questionIndex].dependencies.push({
      questionId: ''
    });
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const removeDependency = (questionIndex, dependencyIndex) => {
    const newQuestions = [...(func.props.questions || [])];
    newQuestions[questionIndex].dependencies = newQuestions[questionIndex].dependencies.filter(
      (_, idx) => idx !== dependencyIndex
    );
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const handleSubQuestionChange = (questionIndex, subQuestionIndex, field, value) => {
    const newQuestions = [...(func.props.questions || [])];
    if (!newQuestions[questionIndex].subQuestions[subQuestionIndex]) {
      newQuestions[questionIndex].subQuestions[subQuestionIndex] = {
        id: generateUUID(),
        headerQuestion: '',
        options: ['Ano', 'Ne']
      };
    }
    newQuestions[questionIndex].subQuestions[subQuestionIndex] = {
      ...newQuestions[questionIndex].subQuestions[subQuestionIndex],
      [field]: value
    };
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const addSubQuestion = (questionIndex) => {
    const newQuestions = [...(func.props.questions || [])];
    if (!newQuestions[questionIndex].subQuestions) {
      newQuestions[questionIndex].subQuestions = [];
    }
    newQuestions[questionIndex].subQuestions.push({
      id: generateUUID(),
      headerQuestion: '',
      options: ['Ano', 'Ne']
    });
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const removeSubQuestion = (questionIndex, subQuestionIndex) => {
    const newQuestions = [...(func.props.questions || [])];
    newQuestions[questionIndex].subQuestions = newQuestions[questionIndex].subQuestions.filter(
      (_, idx) => idx !== subQuestionIndex
    );
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const addQuestion = () => {
    const newQuestions = [...(func.props.questions || [])];
    newQuestions.push({
      id: generateUUID(),
      type: 'question',
      headerQuestion: '',
      options: ['Ano', 'Ne'],
      dependencies: []
    });
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const removeQuestion = (questionIndex) => {
    const newQuestions = func.props.questions.filter((_, idx) => idx !== questionIndex);
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const handleOptionChange = (questionIndex, optionIndex, value, subQuestionIndex = null) => {
    const newQuestions = [...(func.props.questions || [])];
    if (subQuestionIndex !== null) {
      const newOptions = [...newQuestions[questionIndex].subQuestions[subQuestionIndex].options];
      newOptions[optionIndex] = value;
      newQuestions[questionIndex].subQuestions[subQuestionIndex].options = newOptions;
    } else {
      const newOptions = [...newQuestions[questionIndex].options];
      newOptions[optionIndex] = value;
      newQuestions[questionIndex].options = newOptions;
    }
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const addOption = (questionIndex, subQuestionIndex = null) => {
    const newQuestions = [...(func.props.questions || [])];
    if (subQuestionIndex !== null) {
      newQuestions[questionIndex].subQuestions[subQuestionIndex].options.push('');
    } else {
      newQuestions[questionIndex].options.push('');
    }
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const removeOption = (questionIndex, optionIndex, subQuestionIndex = null) => {
    const newQuestions = [...(func.props.questions || [])];
    if (subQuestionIndex !== null) {
      const newOptions = newQuestions[questionIndex].subQuestions[subQuestionIndex].options.filter(
        (_, idx) => idx !== optionIndex
      );
      newQuestions[questionIndex].subQuestions[subQuestionIndex].options = newOptions;
    } else {
      const newOptions = newQuestions[questionIndex].options.filter((_, idx) => idx !== optionIndex);
      newQuestions[questionIndex].options = newOptions;
    }
    onUpdate(index, { ...func.props, questions: newQuestions });
  };

  const toggleQuestion = (questionIndex) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionIndex]: !prev[questionIndex]
    }));
  };

  const renderDependencies = (question, questionIndex) => {
    const getDependentQuestion = (questionId) => {
      const foundQuestion = func.props.questions.find(q => q.id === questionId);
      if (!foundQuestion) return null;
      
      if (foundQuestion.type === 'subQuestions') {
        // For subQuestions, combine all options from all sub-questions
        return {
          ...foundQuestion,
          options: foundQuestion.subQuestions.reduce((acc, sub) => [...acc, ...sub.options], [])
        };
      }
      return foundQuestion;
    };

    return (
      <div className="dependencies-section">
        <label>Dependencies</label>
        {(question.dependencies || []).map((dependency, depIndex) => {
          const dependentQuestion = getDependentQuestion(dependency.questionId);
          return (
            <div key={depIndex} className="dependency-item">
              <div className="dependency-inputs">
                <select
                  value={dependency.questionId}
                  onChange={(e) => handleDependencyChange(questionIndex, depIndex, 'questionId', e.target.value)}
                  className="full-width-input"
                >
                  <option value="">Select Question</option>
                  {func.props.questions.map((q, qIdx) => (
                    qIdx < questionIndex && (
                      <option key={q.id} value={q.id}>
                        {q.headerQuestion || `Question ${qIdx + 1}`}
                      </option>
                    )
                  ))}
                </select>
                {dependentQuestion?.type === 'open' ? (
                  <input
                    type="number"
                    min="1"
                    value={dependency.expectedLength || 1}
                    onChange={(e) => handleDependencyChange(questionIndex, depIndex, 'expectedLength', parseInt(e.target.value))}
                    className="full-width-input"
                    placeholder="Expected Length"
                  />
                ) : dependency.questionId ? (
                  <select
                    value={dependency.expectedResponse || ''}
                    onChange={(e) => handleDependencyChange(questionIndex, depIndex, 'expectedResponse', e.target.value)}
                    className="full-width-input"
                  >
                    <option value="">Select Expected Response</option>
                    {dependentQuestion?.options?.map((opt, optIdx) => (
                      <option key={optIdx} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
              <button
                onClick={() => removeDependency(questionIndex, depIndex)}
                className="remove-button"
                title="Remove dependency"
              >
                ×
              </button>
            </div>
          );
        })}
        <button 
          onClick={() => addDependency(questionIndex)}
          className="add-button"
        >
          + Add Dependency
        </button>
      </div>
    );
  };

  const renderQuestionContent = (question, questionIndex) => {
    return (
      <div className={`question-content ${expandedQuestions[questionIndex] ? 'expanded' : ''}`}>
        <div className="input-group">
          <label>Question Text</label>
          <input
            type="text"
            value={question.headerQuestion || ''}
            onChange={(e) => handleQuestionChange(questionIndex, 'headerQuestion', e.target.value)}
            className="full-width-input"
          />
        </div>

        <div className="input-group">
          <label>Question Type</label>
          <select
            value={question.type}
            onChange={(e) => handleQuestionChange(questionIndex, 'type', e.target.value)}
            className="full-width-input"
          >
            <option value="question">Multiple Choice</option>
            <option value="open">Open Text</option>
            <option value="subQuestions">Sub Questions</option>
          </select>
        </div>

        {question.type === 'open' && (
          <>
            <div className="input-group">
              <label>Placeholder</label>
              <input
                type="text"
                value={question.placeholder || ''}
                onChange={(e) => handleQuestionChange(questionIndex, 'placeholder', e.target.value)}
                className="full-width-input"
              />
            </div>
            <div className="input-group">
              <label>Rows (1-10): {question.rows || 1}</label>
              <input
                type="range"
                min="1"
                max="10"
                value={question.rows || 1}
                onChange={(e) => handleQuestionChange(questionIndex, 'rows', parseInt(e.target.value))}
                className="full-width-input"
              />
            </div>
          </>
        )}

        {question.type === 'question' && (
          <div className="options-section">
            <label>Options</label>
            {(question.options || []).map((option, optionIndex) => (
              <div key={optionIndex} className="option-item">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => handleOptionChange(questionIndex, optionIndex, e.target.value)}
                  className="option-input"
                />
                <button
                  onClick={() => removeOption(questionIndex, optionIndex)}
                  className="remove-option-button"
                  disabled={question.options.length <= 2}
                >
                  ×
                </button>
              </div>
            ))}
            <button 
              onClick={() => addOption(questionIndex)}
              className="add-option-button"
            >
              + Add Option
            </button>
          </div>
        )}

        {question.type === 'subQuestions' && (
          <div className="sub-questions-section">
            <label>Sub Questions</label>
            {(question.subQuestions || []).map((subQuestion, subIndex) => (
              <div key={subQuestion.id} className="sub-question-item">
                <div className="input-group">
                  <label>Sub Question Text</label>
                  <input
                    type="text"
                    value={subQuestion.headerQuestion || ''}
                    onChange={(e) => handleSubQuestionChange(questionIndex, subIndex, 'headerQuestion', e.target.value)}
                    className="full-width-input"
                  />
                </div>
                <div className="options-section">
                  <label>Options</label>
                  {(subQuestion.options || []).map((option, optionIndex) => (
                    <div key={optionIndex} className="option-item">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(questionIndex, optionIndex, e.target.value, subIndex)}
                        className="option-input"
                      />
                      <button
                        onClick={() => removeOption(questionIndex, optionIndex, subIndex)}
                        className="remove-option-button"
                        disabled={subQuestion.options.length <= 2}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button 
                    onClick={() => addOption(questionIndex, subIndex)}
                    className="add-option-button"
                  >
                    + Add Option
                  </button>
                </div>
                <button
                  onClick={() => removeSubQuestion(questionIndex, subIndex)}
                  className="remove-button"
                  title="Remove sub question"
                >
                  ×
                </button>
              </div>
            ))}
            <button 
              onClick={() => addSubQuestion(questionIndex)}
              className="add-button"
            >
              + Add Sub Question
            </button>
          </div>
        )}

        {renderDependencies(question, questionIndex)}
      </div>
    );
  };

  return (
    <div className="attendance-questions-inspector">
      <div className="section-item">
        <div className="section-header" onClick={() => setMessageSettingsExpanded(!messageSettingsExpanded)}>
          <h3>Message Settings</h3>
          <button className={`collapse-button ${messageSettingsExpanded ? 'expanded' : ''}`}>
            ▼
          </button>
        </div>
        {messageSettingsExpanded && (
          <div style={{ marginTop: 24, padding: '0 1rem 1rem 1rem' }}>
            <MessageSectionInspector
              func={{ name: 'MessageSection', props: func.props.messageProps || {} }}
              index={`${index}-message`}
              onUpdate={handleMessageSectionUpdate}
              isIconPickerOpen={isIconPickerOpen}
              setIsIconPickerOpen={setIsIconPickerOpen}
              iconPickerPosition={iconPickerPosition}
              setIconPickerPosition={setIconPickerPosition}
              setActiveIconPreview={setActiveIconPreview}
              bringToFront={bringToFront}
            />
          </div>
        )}
      </div>

      <div className="section-item">
        <div className="section-header" onClick={() => setQuestionsExpanded(!questionsExpanded)}>
          <h3>Questions</h3>
          <button className={`collapse-button ${questionsExpanded ? 'expanded' : ''}`}>
            ▼
          </button>
        </div>
        {questionsExpanded && (
          <div style={{ marginTop: 24, padding: '0 1rem 1rem 1rem' }}>
            {(func.props.questions || []).map((question, questionIndex) => (
              <div key={question.id} className="question-item">
                <div className="question-header" onClick={() => toggleQuestion(questionIndex)}>
                  <h4>{question.headerQuestion || `Question ${questionIndex + 1}`}</h4>
                  <div className="header-buttons">
                    <button className={`collapse-button ${expandedQuestions[questionIndex] ? 'expanded' : ''}`}>
                      ▼
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        removeQuestion(questionIndex);
                      }}
                      className="remove-button"
                      title="Remove question"
                    >
                      ×
                    </button>
                  </div>
                </div>
                {expandedQuestions[questionIndex] && (
                  <div style={{ padding: '1rem' }}>
                    {renderQuestionContent(question, questionIndex)}
                  </div>
                )}
              </div>
            ))}
            
            <button 
              onClick={addQuestion}
              className="add-button"
            >
              + Add Question
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AttendanceQuestionsInspector; 